import { useRestoreMediaFolderMutation } from "@api/media-folders";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { useMediaLibFolderPermission } from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { type IMediaFolder, MediaFolderPermEnum } from "@/shared/types";
import {
  MediaLibFolderArchiveDialog,
  MediaLibFolderDeleteDialog,
  MediaLibFolderMoveDialog,
  MediaLibFolderRenameDialog,
  MediaLibMembersFolderDialog,
} from "./dialogs";

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  folder: IMediaFolder;
  archiveFilesInFolders?: (folderIds: string[]) => void;
}

interface IAction {
  action: "members" | "rename" | "move" | "archive" | "restore" | "delete";
  icon: string;
  label: string;
  permissions?: MediaFolderPermEnum[];
  isArchived?: boolean;
}

const ACTIONS: IAction[] = [
  {
    action: "members",
    icon: "ri-group-line",
    label: "Members",
    permissions: [MediaFolderPermEnum.Owner],
  },
  {
    action: "rename",
    icon: "ri-edit-line",
    label: "Rename folder",
  },
  {
    action: "move",
    icon: "ri-folder-transfer-line",
    label: "Move to folder",
  },
  {
    action: "archive",
    icon: "ri-archive-line",
    label: "Archive folder",
    permissions: [MediaFolderPermEnum.Owner],
  },
  //
  {
    action: "restore",
    icon: "ri-archive-line",
    label: "Restore",
    permissions: [MediaFolderPermEnum.Owner],
    isArchived: true,
  },
  {
    action: "delete",
    icon: "ri-archive-line",
    label: "Delete",
    permissions: [MediaFolderPermEnum.Owner],
    isArchived: true,
  },
];

export const MediaLibFolderActions = ({
  isOpen,
  setIsOpen,
  folder,
  archiveFilesInFolders,
}: IProps) => {
  const userPerm = useMediaLibFolderPermission(folder);

  const filteredActions = useMemo(
    () =>
      ACTIONS.filter(
        (act) =>
          (act.isArchived || false) === folder.archived &&
          (!act.permissions || act.permissions.includes(userPerm)),
      ),
    [folder, userPerm],
  );

  if (filteredActions.length === 0) return null;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={cn({
        "tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-transition-opacity group-hover:tw-opacity-100": true,
        "tw-opacity-0": !isOpen,
      })}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger>
          <button
            type="button"
            className={cn({
              "tw-size-6 tw-cursor-pointer tw-rounded-md tw-border tw-outline-none": true,
              "tw-border-[#E7E6EC]/50 tw-bg-white tw-shadow-main": !isOpen,
              "tw-border-blue-300 tw-bg-blue-200": isOpen,
            })}
          >
            <Icon
              icon="ri-more-fill"
              className="tw-text-[14px]/none tw-text-blue-base"
            />
          </button>
        </Popover.Trigger>

        <Popover.Content className="tw-z-[99999] tw-w-[160px] tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-p-2 tw-shadow-popover">
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-space-y-1">
            {filteredActions.map((act) => (
              <Action
                key={act.action}
                action={act.action}
                icon={act.icon}
                label={act.label}
                folder={folder}
                archiveFilesInFolders={archiveFilesInFolders}
                setIsOpen={setIsOpen}
              />
            ))}
          </ol>
        </Popover.Content>
      </Popover>
    </div>
  );
};

interface IActionProps extends IAction {
  folder: IMediaFolder;
  archiveFilesInFolders?: (folderIds: string[]) => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const Action = ({
  action,
  icon,
  label,
  folder,
  setIsOpen,
  archiveFilesInFolders,
}: IActionProps) => {
  const dispatch = useAppDispatch();

  const [retoreFolder, { isLoading: isUpdatingFolder }] =
    useRestoreMediaFolderMutation();

  const handleAction = async () => {
    if (isUpdatingFolder) return;

    setIsOpen(false);

    switch (action) {
      case "archive":
        dispatch(
          openDialog({
            id: MediaLibFolderArchiveDialog.dialogId,
            content: MediaLibFolderArchiveDialog,
            props: {
              folderData: folder,
              archiveFilesInFolders,
            },
          }),
        );
        break;
      case "members":
        dispatch(
          openDialog({
            id: MediaLibMembersFolderDialog.dialogId,
            content: MediaLibMembersFolderDialog,
            props: {
              folderData: folder,
            },
          }),
        );
        break;
      case "move":
        dispatch(
          openDialog({
            id: MediaLibFolderMoveDialog.dialogId,
            content: MediaLibFolderMoveDialog,
            props: {
              folderData: folder,
            },
          }),
        );
        break;
      case "rename":
        dispatch(
          openDialog({
            id: MediaLibFolderRenameDialog.dialogId,
            content: MediaLibFolderRenameDialog,
            props: {
              folderId: folder._id,
              folderName: folder.title,
            },
          }),
        );
        break;
      case "restore":
        try {
          await retoreFolder({ id: folder._id }).unwrap();
        } catch (e) {
          track("error restoring media folder", {
            folderId: folder._id,
            error: e,
          });
          toast.error("Error restoring folder");
        }

        break;
      case "delete":
        dispatch(
          openDialog({
            id: MediaLibFolderDeleteDialog.dialogId,
            content: MediaLibFolderDeleteDialog,
            props: {
              folderId: folder._id,
              folderName: folder.title,
            },
          }),
        );
        break;
    }
  };

  const isDanger = action === "archive" || action === "delete";
  return (
    <li
      className={cn(
        "tw-flex tw-cursor-pointer tw-items-center tw-space-x-2 tw-rounded tw-px-3 tw-py-[10px] tw-transition-colors",
        isDanger ? "hover:tw-bg-[#FFEEF0]" : "hover:tw-bg-blue-50",
      )}
      onClick={handleAction}
    >
      <Icon
        icon={icon}
        className={cn(
          "tw-text-[16px]/none",
          isDanger ? "tw-text-[#D43548]" : " tw-text-blue-500",
        )}
      />
      <span
        className={cn(
          "tw-text-[11px]",
          isDanger ? "tw-text-[#D43548]" : "tw-text-blue-base",
        )}
      >
        {label}
      </span>
    </li>
  );
};
